import { Controller } from '@hotwired/stimulus';
import {
  disableBackgroundInert,
  enableBackgroundInert,
  lockImageModalPageScroll,
  releaseFocusTrap,
  trapFocus,
  unlockImageModalPageScroll,
} from '../../../utils/FocusTrap';

const GALLERY_MODAL_OPEN_CLASS = 'aqui-image-gallery--modal-open';

const DOT_CURRENT_CLASS = 'aqui-image-gallery-dot--current';
const HIDDEN_CLASS = 'hidden';
const CLOSE_BUTTON_SELECTOR = '[data-railsui-image-gallery-target="modalClose"]';
const CONTROLS_SELECTOR = '[data-railsui-image-gallery-target="modalControls"]';
const PANEL_SELECTOR = '[data-railsui-image-gallery-target="modalPanel"]';
const MODAL_PREV_SELECTOR = '[data-railsui-image-gallery-target="modalPrevArrow"]';
const MODAL_NEXT_SELECTOR = '[data-railsui-image-gallery-target="modalNextArrow"]';
const MODAL_DOT_SELECTOR = '[data-railsui-image-gallery-target="modalDot"]';
const SLIDE_THUMBNAIL_SELECTOR = '[data-railsui-image-gallery-target="slideThumbnail"]';
const SLIDE_CAPTION_SELECTOR = '[data-railsui-image-gallery-target="slideCaption"]';
const SLIDE_EXTERNAL_LINK_SELECTOR = '[data-railsui-image-gallery-target="slideExternalLink"]';

export default class extends Controller {
  static targets = [
    'prevArrow',
    'nextArrow',
    'slide',
    'dot',
    'modal',
    'modalImage',
    'modalCaption',
    'modalExternalLink',
    'modalDot',
    'modalPrevArrow',
    'modalNextArrow',
    'modalClose',
    'modalPanel',
  ];

  static values = {
    showNavigationInModal: Boolean,
  };

  connect() {
    if (this.slideTargets.length === 0) return;

    this.currentIndex = 0;
    this.inertedElements = [];
    this.showSlide(this.currentIndex);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.element.addEventListener('keydown', this.handleKeyDown);

    if (this.showNavigationInModalValue && this.hasModalTarget) {
      this.modalElement = this.modalTarget;
      this.modalImageElement = this.hasModalImageTarget ? this.modalImageTarget : null;
      this.modalCaptionElement = this.hasModalCaptionTarget ? this.modalCaptionTarget : null;
      this.modalExternalLinkElement = this.hasModalExternalLinkTarget ? this.modalExternalLinkTarget : null;
      this.handleModalKeyDown = this.handleModalKeyDown.bind(this);
      this.handleModalClick = this.handleModalClick.bind(this);
      this.portalModal();
      this.modalElement.addEventListener('click', this.handleModalClick);
    }
  }

  disconnect() {
    this.element.removeEventListener('keydown', this.handleKeyDown);

    if (this.modalElement) {
      document.removeEventListener('keydown', this.handleModalKeyDown);
      this.modalElement.removeEventListener('click', this.handleModalClick);
      this.element.classList.remove(GALLERY_MODAL_OPEN_CLASS);
      unlockImageModalPageScroll();
      disableBackgroundInert(this.inertedElements);
      this.inertedElements = [];
      releaseFocusTrap(this.modalElement);
      this.restoreModal();
    }
  }

  prev(event) {
    event?.preventDefault();
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.slideTargets.length - 1;
    this.showSlide(newIndex);
  }

  next(event) {
    event?.preventDefault();
    const newIndex = this.currentIndex < this.slideTargets.length - 1 ? this.currentIndex + 1 : 0;
    this.showSlide(newIndex);
  }

  goToSlide(event) {
    event.preventDefault();
    const index = Number.parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isNaN(index)) return;
    this.showSlide(index);
  }

  openModalWithKey(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

    event.preventDefault();
    this.openModal(event);
  }

  openModal(event) {
    if (!this.showNavigationInModalValue || !this.modalElement) return;

    event?.preventDefault();
    event?.stopPropagation();

    this.lastModalTrigger = this.modalFocusRestoreTarget(event);

    this.syncModalContent();
    this.modalElement.classList.remove(HIDDEN_CLASS);
    this.modalElement.setAttribute('aria-hidden', 'false');
    this.element.classList.add(GALLERY_MODAL_OPEN_CLASS);
    lockImageModalPageScroll();
    this.inertedElements = enableBackgroundInert(this.modalElement);
    document.addEventListener('keydown', this.handleModalKeyDown);

    requestAnimationFrame(() => {
      trapFocus(this.modalElement, { initialFocusSelector: CLOSE_BUTTON_SELECTOR });
    });
  }

  closeModal(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.modalElement) return;

    releaseFocusTrap(this.modalElement);

    this.modalElement.classList.add(HIDDEN_CLASS);
    this.modalElement.setAttribute('aria-hidden', 'true');
    this.element.classList.remove(GALLERY_MODAL_OPEN_CLASS);
    unlockImageModalPageScroll();
    disableBackgroundInert(this.inertedElements);
    this.inertedElements = [];
    document.removeEventListener('keydown', this.handleModalKeyDown);

    if (this.lastModalTrigger && typeof this.lastModalTrigger.focus === 'function') {
      this.lastModalTrigger.focus();
      this.lastModalTrigger = null;
    }
  }

  modalFocusRestoreTarget(event) {
    const trigger = event?.currentTarget;
    if (!trigger) return null;

    if (trigger.getAttribute('tabindex') === '-1') {
      const slide = trigger.closest('[data-railsui-image-gallery-target="slide"]');
      return slide?.querySelector('[aria-label="Expand image"]') ?? trigger;
    }

    return trigger;
  }

  showSlide(index) {
    if (index < 0 || index >= this.slideTargets.length) return;

    this.currentIndex = index;

    this.slideTargets.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('active', isActive);
      slide.toggleAttribute('inert', !isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.updateDotTargets(this.dotTargets, index);

    if (this.showNavigationInModalValue) {
      this.updateDotTargets(this.modalDotElements(), index);

      if (this.modalElement && !this.modalElement.classList.contains(HIDDEN_CLASS)) {
        this.syncModalContent();
      }
    }
  }

  modalDotElements() {
    if (!this.modalElement) return [];

    return Array.from(this.modalElement.querySelectorAll(MODAL_DOT_SELECTOR));
  }

  updateDotTargets(dots, index) {
    dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle(DOT_CURRENT_CLASS, isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  syncModalContent() {
    const slide = this.slideTargets[this.currentIndex];
    if (!slide) return;

    const thumbnail = slide.querySelector(SLIDE_THUMBNAIL_SELECTOR);
    const captionEl = slide.querySelector(SLIDE_CAPTION_SELECTOR);
    const externalLink = slide.querySelector(SLIDE_EXTERNAL_LINK_SELECTOR);

    if (this.modalImageElement && thumbnail) {
      this.modalImageElement.setAttribute('src', thumbnail.getAttribute('src') || '');
      this.modalImageElement.setAttribute('alt', thumbnail.dataset.slideAlt || '');
    }

    if (this.modalCaptionElement) {
      if (captionEl?.textContent?.trim()) {
        this.modalCaptionElement.textContent = captionEl.textContent;
        this.modalCaptionElement.classList.remove(HIDDEN_CLASS);
      } else {
        this.modalCaptionElement.textContent = '';
        this.modalCaptionElement.classList.add(HIDDEN_CLASS);
      }
    }

    if (this.modalExternalLinkElement) {
      if (externalLink?.href) {
        this.modalExternalLinkElement.href = externalLink.href;
        this.modalExternalLinkElement.classList.remove(HIDDEN_CLASS);
      } else {
        this.modalExternalLinkElement.classList.add(HIDDEN_CLASS);
      }
    }
  }

  isGalleryFocused() {
    return this.element.contains(document.activeElement);
  }

  isModalFocused() {
    return this.modalElement?.contains(document.activeElement) ?? false;
  }

  handleKeyDown(event) {
    if (this.modalElement && !this.modalElement.classList.contains(HIDDEN_CLASS)) return;
    if (!this.isGalleryFocused()) return;

    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.showSlide(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.showSlide(this.slideTargets.length - 1);
    }
  }

  handleModalKeyDown(event) {
    if (!this.modalElement || this.modalElement.classList.contains(HIDDEN_CLASS)) return;
    if (!this.isModalFocused()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.showSlide(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.showSlide(this.slideTargets.length - 1);
    }
  }

  handleModalClick(event) {
    if (event.target.closest(CLOSE_BUTTON_SELECTOR)) {
      this.closeModal(event);
      return;
    }

    if (event.target.closest(MODAL_PREV_SELECTOR)) {
      this.prev(event);
      return;
    }

    if (event.target.closest(MODAL_NEXT_SELECTOR)) {
      this.next(event);
      return;
    }

    const modalDot = event.target.closest(MODAL_DOT_SELECTOR);
    if (modalDot) {
      this.goToSlide({ preventDefault: () => {}, currentTarget: modalDot });
      return;
    }

    if (event.target.closest(CONTROLS_SELECTOR)) return;

    if (!event.target.closest(PANEL_SELECTOR)) {
      this.closeModal(event);
    }
  }

  portalModal() {
    if (!this.modalElement || this.modalElement.dataset.portaled === 'true') return;

    this.modalHome = document.createComment('aqui-image-gallery-modal-home');
    this.modalElement.before(this.modalHome);
    document.body.appendChild(this.modalElement);
    this.modalElement.dataset.portaled = 'true';
  }

  restoreModal() {
    if (!this.modalElement || !this.modalHome) return;

    this.modalHome.replaceWith(this.modalElement);
    delete this.modalElement.dataset.portaled;
    this.modalHome = null;
  }
}
